import { Fragment, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Icons, IconType } from '../../../../constants/icons';
import { SVGIconType } from '../../../../constants/svgIcon';
import { RewardReferralRole } from '../../../../models/RewardStore';
import { Profile } from '../../../../models/UserStore';
import {
  AvatarNode,
  SvgIcon,
  UserCell,
  UserCellProps,
} from '../../../Molecules';

import styles from './WebStoreReferralRewardTemplate.module.css';

export type WebStoreReferralRewardTemplateProps = {
  totalReferralUserCount: number;
  profile: Profile | undefined | null;
  referredByProfile: Profile | undefined | null;
  rewardReferralRole: RewardReferralRole | undefined;
  userRewardReferralCode: string | undefined | null;
  availableReward: string;
  confirmReward: string;
  // potentialReward: string | undefined;
  cashOutRequirement: string;
  referralPartnerData: UserCellProps[];
  referralDistributorData: UserCellProps[];
  referralCustomerData: UserCellProps[];
  onClickReferralPopup: () => void;
  onClickCashInfo: () => void;
  onClickRoleDetail: () => void;
  onClickCashOut: () => void;
  onClickAll: (role: RewardReferralRole) => void;
};

export const WebStoreReferralRewardTemplate = memo(
  ({
    totalReferralUserCount,
    profile,
    referredByProfile,
    rewardReferralRole,
    userRewardReferralCode,
    availableReward,
    confirmReward,
    // potentialReward,
    cashOutRequirement,
    referralPartnerData,
    referralDistributorData,
    referralCustomerData,
    onClickReferralPopup,
    onClickCashInfo,
    onClickRoleDetail,
    onClickCashOut,
    onClickAll,
  }: WebStoreReferralRewardTemplateProps) => {
    const { t } = useTranslation();
    return (
      <div className={styles.webStoreReferralReward}>
        <div className={styles.centeredContainer}>
          <div className={styles.headerContainer}>
            <div className={styles.referralText}>
              <span>
                {t('{COUNT} Referred', {
                  COUNT: totalReferralUserCount,
                })}
              </span>
            </div>
            <div className={styles.referralInfo} onClick={onClickReferralPopup}>
              <SvgIcon
                name={SVGIconType.GameInstruction}
                width={20}
                height={20}
                color="#333"
              />
            </div>
          </div>
          <hr className={styles.pageDivider} />
          {referredByProfile ? (
            <div className={styles.referredByContainer}>
              <div className={styles.referredByText}>
                {t('You are referred by: ')}
              </div>
              <AvatarNode profile={referredByProfile} size={30} />
              <div className={styles.referredByName}>
                {referredByProfile.name}
              </div>
            </div>
          ) : null}
          {profile ? (
            <div className={styles.container}>
              <AvatarNode profile={profile} size={80} />
              <div className={styles.column}>
                <div className={styles.userName}>{profile?.name}</div>
                {userRewardReferralCode && (
                  <>
                    <div className={styles.userRoleId}>
                      {`${t(
                        rewardReferralRole === RewardReferralRole.Partner
                          ? 'Partner ID: '
                          : 'Distributor ID: ',
                      )} ${userRewardReferralCode}`}
                    </div>
                    <div
                      className={styles.userRoleDetail}
                      onClick={onClickRoleDetail}
                    >
                      {t(
                        rewardReferralRole === RewardReferralRole.Partner
                          ? 'Partner Details'
                          : 'Distributor Details',
                      )}
                      <SvgIcon
                        name={SVGIconType.Arrow}
                        width={18}
                        height={18}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : null}

          {!rewardReferralRole ||
          rewardReferralRole === RewardReferralRole.Customer ? (
            <div className={styles.whiteBanner}>
              <div className={styles.columnBannerText}>
                <div className={styles.joyCashContainer}>
                  <img
                    src={Icons[IconType.Credit]}
                    className={styles.joyCashIcon}
                  />
                  <div className={styles.joyCashText}>
                    <span>{t('Cash Credit')}</span>
                  </div>
                  <SvgIcon
                    name={SVGIconType.GameInstruction}
                    className={styles.joyCashInfo}
                    onClick={onClickCashInfo}
                  />
                </div>
                <div className={styles.centerText}>
                  <div className={styles.rewardTotalText}>
                    {t('Available Rewards (Total): ')}
                    <img
                      src={Icons[IconType.Credit]}
                      className={styles.smallJoyCashIcon}
                    />
                    <span className={styles.confirmedSpan}>
                      {availableReward}
                    </span>
                  </div>
                  <div className={styles.rewardTotalText}>
                    {t('Confirmed Rewards (Total): ')}
                    <img
                      src={Icons[IconType.Credit]}
                      className={styles.smallJoyCashIcon}
                    />
                    <span className={styles.potentialSpan}>
                      {confirmReward}
                    </span>
                  </div>
                </div>

                <button
                  className={styles.cashOutButton}
                  onClick={onClickCashOut}
                >
                  {t('Purchase JOYAJOY Products')}
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.whiteBanner}>
              <div className={styles.columnBannerText}>
                <div className={styles.joyCashContainer}>
                  <SvgIcon
                    name={SVGIconType.JOYCashWeb}
                    className={styles.joyCashIcon}
                  />
                  <div className={styles.joyCashText}>
                    <span>{t('JOY Cash')}</span>
                  </div>
                  <SvgIcon
                    name={SVGIconType.GameInstruction}
                    className={styles.joyCashInfo}
                    onClick={onClickCashInfo}
                  />
                </div>
                <div className={styles.centerText}>
                  <div className={styles.rewardTotalText}>
                    {t('Available Rewards (Total): ')}
                    <span className={styles.confirmedSpan}>
                      {availableReward}
                    </span>
                  </div>
                  <div className={styles.rewardTotalText}>
                    {t('Confirmed Rewards (Total): ')}
                    <span className={styles.potentialSpan}>
                      {confirmReward}
                    </span>
                  </div>
                  {/* {potentialReward !== undefined ? (
                    <div className={styles.rewardTotalText}>
                      {t('Confirmed Management Rewards (Total): ')}
                      <span className={styles.potentialSpan}>
                        {potentialReward}
                      </span>
                    </div>
                  ) : null} */}
                </div>

                <button
                  className={styles.cashOutButton2}
                  onClick={onClickCashOut}
                >
                  {t('Cash Out')}
                </button>
                <div className={styles.rewardDescription}>
                  <p className={styles.rewardDescriptionText}>
                    {t(
                      '* You need to accumulate at least {amount} to cash out.',
                      { amount: cashOutRequirement },
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {(rewardReferralRole === RewardReferralRole.Partner ||
            referralPartnerData.length > 0) && (
            <UserCellList
              title="Referred Partners"
              data={referralPartnerData}
              onClickAll={onClickAll}
            />
          )}

          {(rewardReferralRole === RewardReferralRole.Partner ||
            referralDistributorData.length > 0) && (
            <UserCellList
              title="Referred Distributor"
              data={referralDistributorData}
              onClickAll={onClickAll}
            />
          )}

          {rewardReferralRole !== RewardReferralRole.Partner && (
            <UserCellList
              title="Referred Users (Tier 1)"
              data={referralCustomerData}
              onClickAll={onClickAll}
            />
          )}
        </div>
      </div>
    );
  },
);

type UserCellListProps = {
  title: string;
  data: UserCellProps[];
  onClickAll: (role: RewardReferralRole) => void;
};

const UserCellList = memo(({ title, data, onClickAll }: UserCellListProps) => {
  const { t } = useTranslation();
  return (
    <>
      <div className={styles.listTitle}>
        {t(title)}
        {data.length > 3 && (
          <div
            className={styles.allButton}
            onClick={() => {
              onClickAll(RewardReferralRole.Customer);
            }}
          >
            <span>{t('All')}</span>
            <SvgIcon
              name={SVGIconType.Arrow}
              color="#808080"
              width={16}
              height={16}
            />
          </div>
        )}
      </div>
      <div className={styles.listView}>
        {data.length === 0 ? (
          <div className={styles.emptyContainer}>
            <SvgIcon
              name={SVGIconType.DoublePerson}
              className={styles.emptyReferralIcon}
            />
            <div className={styles.emptyReferralText}>
              {t('No Users Referred')}
            </div>
          </div>
        ) : (
          data.slice(0, 3).map((item, index) => (
            <Fragment key={index}>
              {index != 0 && <div className={styles.cellSpace} />}
              <UserCell key={index} {...item} />
            </Fragment>
          ))
        )}
      </div>
    </>
  );
});
